'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AuthError } from '@azure/msal-browser';
import Image from 'next/image';
import { AppSpinner } from '@/components/layout/app-spinner';
import { useThemeStore } from '@/stores/theme-store';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';


export default function LoginPage() {
  const { instance, accounts, inProgress } = useMsal();
  const router = useRouter();
  const isAuthenticated = useIsAuthenticated();
  const initialRenderRef = useRef(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  
  const { colorScheme, darkMode } = useThemeStore();

  const redirectToMain = useCallback(() => {
    if (!isRedirecting) {
      setIsRedirecting(true);
      
      setTimeout(() => {
        const intendedUrl = localStorage.getItem('intendedUrl');
        if (intendedUrl) {
          localStorage.removeItem('intendedUrl');
          router.push(intendedUrl);
        } else {
          router.push('/main');
        }
      }, 300);
    }
  }, [router, isRedirecting]);

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      setIsCheckingAuth(true);
      setShowLoginForm(false);

      const timer = setTimeout(() => {
        checkAuthAndRedirect();
      }, 80);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const checkAuthAndRedirect = async () => {
    try {
      if (isAuthenticated && !isRedirecting) {
        redirectToMain();
      } else if (accounts.length > 0 && !isRedirecting) {
        try {
          await instance.ssoSilent({
            account: accounts[0],
            scopes: ['openid', 'profile', 'email']
          });
          redirectToMain();
        } catch (error) {
          console.error('Silent login failed:', error);
          setIsCheckingAuth(false);
          setShowLoginForm(true);
        }
      } else {
        setIsCheckingAuth(false);
        setShowLoginForm(true);
      }
    } catch (error) {
      console.error('Error during auth check:', error);
      setIsCheckingAuth(false);
      setShowLoginForm(true);
    }
  };

  useEffect(() => {
    if (!initialRenderRef.current && !isRedirecting) {
      checkAuthAndRedirect();
    }
  }, [isAuthenticated, accounts, isRedirecting]);

  useEffect(() => {
    if (inProgress === 'none' && isSigningIn) {
      setTimeout(() => {
        setIsSigningIn(false);
        if (!isAuthenticated && !isRedirecting) {
          setShowLoginForm(true);
        }
      }, 500);
    }
  }, [inProgress, isSigningIn, isAuthenticated, isRedirecting]);

  const handleLogin = async () => {
    setLoginError(null);
    setIsSigningIn(true);
    setShowLoginForm(false);
    
    if (inProgress !== 'none') {
      return;
    }
    
    try {
      await instance.loginRedirect({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account'
      });
    } catch (error) {
      console.error('Login failed:', error);
      
      if (error instanceof AuthError && error.errorCode === 'interaction_in_progress') {
        setLoginError('Authentication already in progress. Please wait or refresh the page.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
  };

  const handleFadeComplete = () => {
  };

  if (isCheckingAuth || isRedirecting || isSigningIn || !showLoginForm) {
    let message = "Checking authentication status...";
    if (isRedirecting) {
      message = "Redirecting to application...";
    } else if (isSigningIn) {
      message = "Signing in...";
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <AppSpinner
            size="lg"
            variant="default"
            message={message}
            fadeOut={isRedirecting}
            onFadeComplete={handleFadeComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen flex items-center justify-center",
      "bg-background",
      `theme-${colorScheme}`,
      darkMode ? "dark" : ""
    )}>
      <div className="relative w-[500px] mx-auto bg-card rounded-[2rem] shadow-xl p-8 border border-border/5 flex flex-col items-center z-10">
        <div className="text-center mb-6">
          <Image
            src="/app-login-logo.png"
            alt="Gulf Distributing"
            width={200}
            height={50}
            priority
            className="mx-auto"
          />
          <h1 className="text-lg font-medium mt-3 text-foreground">
            Welcome to Gulfstream
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">Please sign in to continue</p>
          <p className="mt-1 text-muted-foreground/60 text-xs">Version {process.env.NEXT_PUBLIC_APP_VERSION} - {new Date().toLocaleString()}</p>
        </div>
        
        {loginError && (
          <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            {loginError}
          </div>
        )}
        
        <div className="relative flex justify-center mt-4 mb-2">
          <Button
            onClick={handleLogin}
            variant="default"
            disabled={inProgress !== 'none'}
            size="lg"
            className="w-[220px]"
            aria-label="Sign in with Microsoft"
          >
            {inProgress !== 'none' ? 'Signing in...' : 'Sign in with Microsoft'}
          </Button>
        </div>
      </div>
    </div>
  );
}
